
import React from "react";
import { Star, Tag, Clock, Package, Hash, Building2, Image, Link, MessageSquare, ChevronRight } from "lucide-react";

interface ProductCardProps {
  productName: string;
  description: string;
  price: string;
  rating: number;
  reviews: number;
  availability: string;
  mpn: string;
  brand: string;
  imageUrl: string;
  productUrl: string;
  breadcrumb: string[];
  customerReview?: string;
  reviewAuthor?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({
  productName,
  description,
  price,
  rating,
  reviews,
  availability,
  mpn,
  brand,
  imageUrl,
  productUrl,
  breadcrumb,
  customerReview,
  reviewAuthor,
}) => {
  // Function to display stars
  const renderStars = (rating: number) => {
    return Array(5)
      .fill(0)
      .map((_, index) => (
        <Star
          key={index}
          className={`h-4 w-4 ${
            index < Math.floor(rating)
              ? "text-yellow-400 fill-yellow-400"
              : index < rating
              ? "text-yellow-400 fill-yellow-400 opacity-50"
              : "text-gray-300"
          }`}
        />
      ));
  };

  return (
    <div className="max-w-4xl mx-auto overflow-hidden rounded-xl shadow-lg bg-gradient-to-br from-slate-900 to-slate-800 text-white border border-blue-900/30 relative">
      {/* Decorative tech-style elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -z-10"></div>
      
      {/* Tech background grid */}
      <div className="absolute inset-0 bg-grid-dark opacity-30 -z-10"></div>

      {/* Header with image and main information */}
      <div className="p-6 flex flex-col md:flex-row gap-8">
        <div className="w-full md:w-1/3">
          <div className="bg-gradient-to-br from-slate-800 to-slate-700 p-4 rounded-lg border border-slate-700 shadow-inner flex items-center justify-center h-64">
            <img
              src={imageUrl || "/public/lovable-uploads/6ab3556b-2793-445d-a903-90845bced60e.png"}
              alt={productName}
              className="max-h-full max-w-full object-contain hover:scale-105 transition-transform duration-300"
            />
          </div>
        </div>

        <div className="w-full md:w-2/3 space-y-4">
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-gradient-tech">{productName}</h1>
            <p className="text-slate-300">{description}</p>
            <div className="flex items-center space-x-2">
              <div className="flex">{renderStars(rating)}</div>
              <span className="text-slate-300">({reviews} reviews)</span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-3xl font-bold text-white">{price}</span>
            <span className="px-3 py-1 bg-green-600/20 text-green-400 text-sm rounded-full border border-green-500/30">
              {availability}
            </span>
          </div>

          <div className="pt-4 space-y-2">
            <button className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg shadow-lg shadow-blue-900/30 transition-all duration-300 flex items-center justify-center gap-2">
              <span>Add to Cart</span>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Detailed information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 bg-slate-900/50 border-t border-blue-900/20">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Tag className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <h3 className="text-sm text-slate-400">Product name</h3>
              <p className="text-white">{productName}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <h3 className="text-sm text-slate-400">Availability</h3>
              <p className="text-white">{availability}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Hash className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <h3 className="text-sm text-slate-400">MPN/GTIN/SKU</h3>
              <p className="text-white">{mpn}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Package className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <h3 className="text-sm text-slate-400">Breadcrumb</h3>
              <p className="text-slate-300 text-sm">
                {breadcrumb.join(" > ")}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Star className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <h3 className="text-sm text-slate-400">Rating</h3>
              <div className="flex items-center gap-2">
                <p className="text-white">{rating}/5</p>
                <span className="text-sm text-slate-400">({reviews} reviews)</span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Building2 className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <h3 className="text-sm text-slate-400">Brand</h3>
              <p className="text-white">{brand}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Image className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <h3 className="text-sm text-slate-400">Image URL</h3>
              <p className="text-blue-400 text-sm truncate max-w-xs">
                {imageUrl}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Link className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <h3 className="text-sm text-slate-400">Product URL</h3>
              <a href={productUrl} className="text-blue-400 text-sm truncate block max-w-xs hover:underline">
                {productUrl}
              </a>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <MessageSquare className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <h3 className="text-sm text-slate-400">Customer reviews</h3>
              {customerReview ? (
                <div className="mt-1 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <p className="text-sm text-slate-300 italic">"{customerReview}"</p>
                  {reviewAuthor && (
                    <p className="text-xs text-slate-400 mt-1">- {reviewAuthor}</p>
                  )}
                </div>
              ) : (
                <p className="text-slate-400 text-sm">No reviews yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 bg-slate-950/50 text-center text-xs text-slate-500 border-t border-blue-900/20">
        <p>All data is structured and ready for integration into your analytics systems - no processing required.</p>
      </div>
    </div>
  );
};

export default ProductCard;
